import React, { useState, useRef, useEffect } from 'react';
import { API, getAuthHeaders } from '../App';
import { MapPin, Search, Loader2 } from 'lucide-react';

export default function CourseSearchInput({ value, onChange, placeholder, className }) {
  const [query, setQuery] = useState(value || '');
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const timeoutRef = useRef(null);
  const wrapperRef = useRef(null);

  useEffect(() => {
    setQuery(value || '');
  }, [value]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        setShowDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('touchstart', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('touchstart', handleClickOutside);
    };
  }, []);

  const searchCourses = (q) => {
    setQuery(q);
    onChange && onChange(q, null);
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    if (q.length < 2) { setResults([]); setShowDropdown(false); return; }
    setSearching(true);
    timeoutRef.current = setTimeout(async () => {
      try {
        const res = await fetch(`${API}/courses/search?query=${encodeURIComponent(q)}`, { credentials: 'include', headers: getAuthHeaders() });
        const data = await res.json();
        const courses = Array.isArray(data) ? data : data.courses || [];
        setResults(courses.slice(0, 8));
        setShowDropdown(courses.length > 0);
      } catch {
        setResults([]);
      }
      setSearching(false);
    }, 350);
  };

  const selectCourse = (course) => {
    const loc = course.location || {};
    const name = course.club_name || course.course_name || '';
    const location = [loc.city, loc.state].filter(Boolean).join(', ');
    const display = location ? `${name} — ${location}` : name;
    setQuery(display);
    setShowDropdown(false);
    setResults([]);
    onChange && onChange(display, course);
  };

  return (
    <div ref={wrapperRef} className="relative" data-testid="course-search-input">
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
        <input
          type="text"
          value={query}
          onChange={e => searchCourses(e.target.value)}
          onFocus={() => results.length > 0 && setShowDropdown(true)}
          placeholder={placeholder || "Search golf courses..."}
          className={`w-full pl-9 pr-8 py-2.5 bg-gray-50 border border-gray-200 rounded-lg font-body text-sm focus:outline-none focus:border-bundle-green focus:ring-1 focus:ring-bundle-green/30 ${className || ''}`}
          data-testid="course-search-field"
        />
        {searching && <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 animate-spin text-gray-400" />}
      </div>

      {showDropdown && results.length > 0 && (
        <div className="absolute z-50 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-56 overflow-y-auto" data-testid="course-search-results">
          {results.map(course => {
            const loc = course.location || {};
            return (
              <button
                key={course.id}
                onClick={() => selectCourse(course)}
                className="w-full text-left px-3 py-2.5 hover:bg-bundle-green/5 transition-colors flex items-start gap-2.5 border-b border-gray-50 last:border-0"
                data-testid={`course-result-${course.id}`}
              >
                <MapPin className="w-4 h-4 text-bundle-green mt-0.5 flex-shrink-0" />
                <div className="min-w-0">
                  <p className="font-body text-sm font-semibold text-gray-800 truncate">{course.club_name || course.course_name}</p>
                  {course.course_name && course.club_name !== course.course_name && (
                    <p className="font-body text-xs text-gray-500 truncate">{course.course_name}</p>
                  )}
                  <p className="font-body text-xs text-gray-400 truncate">
                    {[loc.city, loc.state, loc.country].filter(Boolean).join(', ')}
                  </p>
                </div>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
